import * as React from 'react';
import { Field } from '../form-controls/Field';
import { TextInput as TextInputStandard } from '@emorg-prd/standard-react';
import { IValidation, Validation } from '../../../utils/Validation';
import './TextInput.css'

export enum TextInputState {
    Success = 'em-is-valid',
    Disabled = 'em-is-disabled',
    Error = 'em-has-error',
    ReadOnly = 'em-is-readonly'
}

export enum TextInputSize {
    Default = '',
    Small = 'em-c-field--small',
}

export type TextInputVariantSize = 'small' | 'default';

export type TextInputVariantState = "disabled" | "success" | "read-only" | "error";

const typeMap: Record<TextInputVariantState, TextInputState> = {
    'success': TextInputState.Success,
    'disabled': TextInputState.Disabled,
    'error': TextInputState.Error,
    'read-only': TextInputState.ReadOnly,
};

const sizeMap: Record<TextInputVariantSize, TextInputSize> = {
    'default': TextInputSize.Default,
    'small': TextInputSize.Small,
};

interface TextInputProps extends Omit<React.ComponentProps<typeof TextInputStandard>, 'size' | 'state' | 'optionalClass'> {
	className?: string;
	size?: TextInputVariantSize;
	state?: TextInputState;
	disabled?: boolean;
	validation?: IValidation;
	valid?: boolean;
	error?: boolean;
	readOnly?: boolean;  
}

const TextInput: React.FC<TextInputProps> = (props) => {
	const abstractTextInput = new (class extends Field<TextInputProps> {
		constructor() {
			super({
				onChange: props.onChange,
				value: props.value,
				validation: props.validation,
			});
		}
	})();

	const computeState = (): TextInputState | undefined => {
		if (props.disabled) return TextInputState.Disabled;
		if (props.readOnly) return TextInputState.ReadOnly;
		if (props.error === true) return TextInputState.Error;
		if (props.valid === true) return TextInputState.Success;

		if (props.validation) {
			const v = new Validation(props.validation);
			return v.assert(props.value) ? TextInputState.Success : TextInputState.Error;			
		}

		return undefined;
	};

	const mappedState = props.state ?? computeState();
	
	const mappedSize: React.ComponentProps<typeof TextInputStandard>['size'] =
    props.size === 'small' ? 'em-c-field--small' : undefined


	const { className, size, valid, error, readOnly, disabled, state, note, ...rest } = props;

	return (
		<TextInputStandard
			{...props}
			optionalClass={props.className}
			state={mappedState}
			size={mappedSize}
			note={abstractTextInput.nonValidMessage() || props.note}
		/>
	);
};

export { TextInput };
